<template>
  <template v-if="restricted !== RESTRICTION.NOT_READY">
    <media-query-provider :queries="queries">
      <site-is-not-available-in-this-region v-if="restricted === RESTRICTION.SERVICE_NOT_ALLOWED" />
      <technical-work v-else-if="showUnderDev" />
      <template v-else>
        <router-view />
        <custom-modal v-if="appModal.isActive" v-model="appModal.isActive" v-bind="appModal.props" />
        <modals-container />
      </template>
    </media-query-provider>
  </template>
</template>

<script setup>
import {
  computed,
  defineAsyncComponent,
  markRaw,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useGtm } from '@gtm-support/vue-gtm'
import { ApiServiceMain } from 'boot/api.js'
import { useHelpers } from 'common/composition/useHelpers.js'
import { usePWA } from 'common/composition/usePWA.js'
import { getCashBoxModal } from 'common/lazy-components.js'
import { nativeBridge } from 'common/native-bridge.js'
import { showViaAppModal } from 'common/utils/appModalPopup.js'
import { ModalsContainer } from 'vue-final-modal'
import { useMainSlider } from 'components/banners/useMainSlider.js'
import MediaQueryProvider from 'components/MediaQueryProvider.vue'
import SiteIsNotAvailableInThisRegion from 'components/technical/SiteIsNotAvailableInThisRegion.vue'
import TechnicalWork from 'components/technical/TechnicalWork.vue'
import { useLazyQueryApp, useQueryApp } from 'hooks/apolloQuery.js'
import { useModalQuery } from 'hooks/useModalQuery.js'
import { reachCheckpoint } from 'hooks/usePerfStep.js'
import { useTelegramMiniApp } from 'hooks/useTelegramMiniApp.js'
import { useHandleQueries } from 'src/hooks/query'
import { useAntibot } from 'src/hooks/useAntibot'
import { useAntibotRuntimeContext } from 'src/hooks/useAntibotRuntimeContext'
import { useAutologin } from 'src/hooks/useAutologin'
import { useMetadata } from 'src/hooks/useMetadata'
import { usePollingUpdate } from 'src/hooks/usePollingUpdate'
import { useMainStore } from 'stores/main.js'
import { POPUP_PRIORITY, usePopupQueueStore } from 'stores/popup-queue.js'
import { useServerStatusStore } from 'stores/server-status'
import { useLoyaltyStore } from 'src/stores/loyalty'
import { usePaymentsStore } from 'src/stores/payments'
import { usePromoStore } from 'src/stores/promo'
import { useSiteInfoStore } from 'src/stores/site-info'
import { useSystemStore } from 'src/stores/system'
import { useUserAuthStore } from 'src/stores/user/auth'
import { useUserBonusesStore } from 'src/stores/user/bonuses'
import afpl from 'src/common/AppsFlyerParseLink'
import { BASE_URL, LIST_OF_GET_PARAMS_TO_CLEAR, LOCATIONS_LIST } from 'src/common/config'
import { APP_IDS, APP_TYPES, MB_UUIDS, RESTRICTION, UNDER_DEV } from 'src/common/dictionary'
import { consoleLogAPI, preloaderVisible, prewarmFingerprint } from 'src/common/functions'
import { scheduleNonCriticalTask } from 'src/common/non-critical-task'
import { metrics } from 'src/common/sentry-client'
import { setTag } from 'src/common/sentry-client'
import { localeForExternalService } from 'src/common/utils/locale'
import { dga } from 'src/common/websocket-dga'
import { defineApp, externalLinkMessageToApp } from 'src/common/webview'
import { postMessageFromWebviewToApp } from 'src/common/webview'
import { getConfig } from 'src/configs'
import CustomModal from 'src/core/components/customModal/CustomModal.vue'
import { webviewRouteNames } from 'src/router/webview-routes'
import { warmupRecaptchaScript } from 'src/services/recaptcha'
import getAllPaymentMethods from 'src/apollo/queries/getAllPaymentMethods.gql'
import getContent from 'src/apollo/queries/getContent.gql'
import getContentList from 'src/apollo/queries/getContentList.gql'
import userPromo from 'src/apollo/queries/userPromo.gql'
const queries = {
  mobileNano: '(max-width: 359px)',
  mobileSmall: '(max-width: 575px)',
  mobile: '(max-width: 767px)',
  landscapeMobile: '(max-height: 450px) and (orientation: landscape)',
  landscape: '(orientation: landscape)',
  tab: '(min-width: 768px)',
  notDesktop: '(max-width: 1023px)',
  desktop: '(min-width: 1024px)',
  desktopMedium: '(min-width: 1200px)',
  desktopBig: '(min-width: 1440px)',
}

reachCheckpoint('app.vue_start')

const $helpers = useHelpers()
const PWA = usePWA()
const config = getConfig()
const $gtm = useGtm()
const route = useRoute()
const router = useRouter()
const { locale } = useI18n()

const mainStore = useMainStore()
const paymentsStore = usePaymentsStore()
const promoStore = usePromoStore()
const systemStore = useSystemStore()
const userAuthStore = useUserAuthStore()
const siteInfoStore = useSiteInfoStore()
const userBonusesStore = useUserBonusesStore()
const serverStatusStore = useServerStatusStore()
const loyaltyStore = useLoyaltyStore()
const NON_CRITICAL_TIMEOUT_MS = 2500
const NON_CRITICAL_FALLBACK_DELAY_MS = 500
const NON_CRITICAL_WAVE_DELAY_MS = 600

const { load: getContentLoad } = useLazyQueryApp(getContent)
const { load: getContentListLoad } = useLazyQueryApp(getContentList)
const { load: userPromoLoad } = useLazyQueryApp(userPromo, {}, { fetchPolicy: 'network-only' })
const appModal = ref({
  isActive: false,
  props: null,
  dwhError: (event) => {
    dwhSendEvent(event)
  },
  open: async function (props) {
    this.isActive = true
    return new Promise((resolve, reject) => {
      this.props = { ...props, actions: props.actions, resolve, reject }
    })
      .catch(() => {
        if (props.props?.tab === 'login') {
          this.dwhError('click_close_auth_form')
        } else if (props.props?.tab === 'reg') {
          this.dwhError('click_close_reg_form')
        }
      })
      .finally(() => {
        this.props = null
      })
  },
  close: function () {
    this.isActive = false
  },
})

const popupQueue = usePopupQueueStore()
const pendingDecline = ref(null)
popupQueue.register({
  id: 'deposit',
  priority: POPUP_PRIORITY.deposit,
  isReady: () => pendingDecline.value != null && route.name !== 'GamePageNew',
  show: () => {
    const { oldMoneyOrderUuid } = pendingDecline.value
    pendingDecline.value = null
    return showViaAppModal(appModal.value, {
      componentName: markRaw(defineAsyncComponent(() => import('components/modals/RepeatTopUpModal.vue'))),
      props: { oldMoneyOrderUuid },
    }).finally(() => router.replace({ query: null }))
  },
})

const { initTelegramMiniApp, shouldInitTelegramMiniApp, syncTelegramMiniApp } = useTelegramMiniApp({
  onLogin: () => dwhSendEvent('login_miniapp'),
})

const getAuth = computed(() => userAuthStore.getAuth)
const getUser = computed(() => userAuthStore.getUser)
const isMobile = computed(() => systemStore.isMobile)
const isWebView = computed(() => siteInfoStore.isWebView)
const userCurrency = computed(() => siteInfoStore.userCurrency)
const getCountryCode = computed(() => siteInfoStore.getCountryCode)

const { onResult: onPaymentMethodsResult, loading: paymentMethodsLoading } = useQueryApp(
  getAllPaymentMethods,
  null,
  () => ({
    enabled: systemStore.altW && getAuth.value,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  }),
)

onPaymentMethodsResult(({ data }) => {
  paymentsStore.updatePaymentMethods(data?.payments)
})

watch(
  paymentMethodsLoading,
  (value) => {
    paymentsStore.updatePaymentMethodsLoading(value)
  },
  { immediate: true },
)

watch(getAuth, (value) => {
  if (!value) {
    paymentsStore.clearPaymentMethods()
  }
})

const isLiveChatPage = computed(() => route.name === 'livechat')
const webViewRouteNames = new Set(webviewRouteNames)
const isWebViewPage = computed(() => webViewRouteNames.has(route.name))

const restricted = computed(() => serverStatusStore.restricted)
const showUnderDev = computed(() => serverStatusStore.underDev === UNDER_DEV.ACTIVE || !serverStatusStore.serverIsOk)

if (restricted.value === RESTRICTION.SERVICE_NOT_ALLOWED || showUnderDev.value) {
  preloaderVisible(false)
}

const antibotRuntimeContext = useAntibotRuntimeContext()
scheduleNonCriticalTask(() => useAntibot(antibotRuntimeContext))

useHandleQueries()
usePollingUpdate(appModal.value)
useMetadata()
useModalQuery(appModal.value)

useMainSlider()

if (isWebView.value) {
  window.__fromNative = function (msg) {
    if (!msg || !msg.type) {
      return
    }
    nativeBridge.emit(msg.type, msg.payload)
  }

  useAutologin({ dwh: dwhSendEvent })

  nativeBridge.on('OPEN_CASHBOX_MODAL', (tab = 'topup') => {
    appModal.value.open({
      componentName: getCashBoxModal(),
      props: { initTab: tab },
    })
  })
}

function setGtmUser() {
  if (!getUser.value?.id || !$gtm) {
    return
  }

  $gtm.push({
    event: 'userLoggedIn',
    user_id: getUser.value.id,
  })
}

function runNonCriticalTask(
  task,
  { timeout = NON_CRITICAL_TIMEOUT_MS, fallbackDelay = NON_CRITICAL_FALLBACK_DELAY_MS, startDelay = 0 } = {},
) {
  scheduleNonCriticalTask(task, {
    timeout,
    fallbackDelay,
    delay: startDelay,
  })
}

function runNonCriticalTaskInWave(task, wave = 0, options = {}) {
  runNonCriticalTask(task, {
    ...options,
    startDelay: Math.max(0, wave) * NON_CRITICAL_WAVE_DELAY_MS,
  })
}

function initRecaptchaWarmupAfterInteraction() {
  let isWarmed = false

  const cleanup = () => {
    window.removeEventListener('pointerdown', warmup)
    window.removeEventListener('keydown', warmup)
    window.removeEventListener('touchstart', warmup)
    window.removeEventListener('scroll', warmup)
  }

  const warmup = () => {
    if (isWarmed) {
      return
    }
    isWarmed = true
    cleanup()
    warmupRecaptchaScript()
  }

  window.addEventListener('pointerdown', warmup, { once: true, passive: true })
  window.addEventListener('keydown', warmup, { once: true })
  window.addEventListener('touchstart', warmup, { once: true, passive: true })
  window.addEventListener('scroll', warmup, { once: true, passive: true })
}

function initFingerprintWarmupAfterInteraction() {
  let isScheduled = false

  const cleanup = () => {
    window.removeEventListener('pointerdown', warmup)
    window.removeEventListener('keydown', warmup)
    window.removeEventListener('touchstart', warmup)
    window.removeEventListener('scroll', warmup)
  }

  const warmup = () => {
    if (isScheduled) {
      return
    }

    isScheduled = true
    cleanup()

    // Fingerprint is not critical for first paint; keep a gap from captcha warmup.
    runNonCriticalTask(prewarmFingerprint, {
      startDelay: 5000,
      timeout: 3000,
      fallbackDelay: 500,
    })
  }

  window.addEventListener('pointerdown', warmup, { once: true, passive: true })
  window.addEventListener('keydown', warmup, { once: true })
  window.addEventListener('touchstart', warmup, { once: true, passive: true })
  window.addEventListener('scroll', warmup, { once: true, passive: true })
}

function getIsShowBigWins() {
  getContentLoad(null, {
    type: 'isShowBigWins',
    title: process.env.PLATFORM.toLowerCase() + '_is_show_big_wins',
  })
    .then((res) => {
      if (res?.propsContent?.props) {
        const isShowBigWins = JSON.parse(res.propsContent.props)?.isShowBigWins
        promoStore.setIsShowBigWins(isShowBigWins)
      }
    })
    .catch(consoleLogAPI)
}

function dwhSendEvent(event, params = {}) {
  if (!config.useDWH) {
    return
  }

  const isIosWebview = location.hostname === 'apps-olymp.xyz'

  const origin = {
    APP_IOS: 'APP_IOS',
    APP_ANDROID: 'APP_ANDROID',
    BROWSER_MOBILE: 'BROWSER_MOBILE',
    BROWSER_DESKTOP: 'BROWSER_DESKTOP',
  }

  let device = 'UNDEFINED'

  if (defineApp() === 'map' && isWebView.value) {
    device = origin.APP_ANDROID
  } else if (defineApp() === 'mav' && isWebView.value) {
    device = origin.APP_IOS
  } else if (isIosWebview && isWebView.value) {
    device = origin.APP_IOS
  } else if (isMobile.value && !isWebView.value) {
    device = origin.BROWSER_MOBILE
  } else if (!isMobile.value && !isWebView.value) {
    device = origin.BROWSER_DESKTOP
  }

  return ApiServiceMain.dwh.sendEvent(event, getCountryCode.value, device, params).catch(consoleLogAPI)
}

function handleClickToCatchExternalLinks(event) {
  const target = event.target.closest('a')
  if (target && target.href) {
    const url = new URL(target.href)
    if (url.origin !== window.location.origin) {
      event.preventDefault()
      externalLinkMessageToApp(url.toString())
    }
  }
}

function handleClickToCatchAnalyticsData(event) {
  const target = event.target.closest('[data-analytics-id]')
  if (target) {
    const dataset = { ...target.dataset }
    if (dataset.analyticsId) {
      dwhSendEvent('click-analytics-id', {
        additionalData: {
          element: dataset.analyticsId,
        },
      })
      if ($gtm) {
        $gtm.trackEvent({
          event: 'click-analytics-id',
          category: 'Click',
          action: 'click',
          label: 'Click Analytics Id',
          value: dataset.analyticsId,
        })
      }
    }
  }
}

function handleAllClicks(event) {
  handleClickToCatchAnalyticsData(event)
  if (isWebView.value) {
    handleClickToCatchExternalLinks(event)
  }
}

function listenIframe(event) {
  const eventData = event?.data
  if (!eventData) {
    return
  }
  if (eventData === 'geo_blocking_get_lang') {
    event.source.postMessage({ name: 'geo_blocking_lang', lang: localeForExternalService(locale.value) }, '*')
  }

  if (eventData === 'geo_blocking_back') {
    const prevRoute = systemStore.prevRoute?.name ? systemStore.prevRoute : { name: 'Home' }
    router.replace(prevRoute).catch(() => {})
  }

  const arrayHiddenTypes = ['application/x-postmate-v1+json', 'action-hook-fired']
  const arrayHiddenMessage = ['action-hook-fired', 'reload-tab-complete']

  if (
    localStorage.getItem('show-events') &&
    !arrayHiddenTypes.includes(eventData?.type) &&
    !arrayHiddenMessage.includes(eventData?.message)
  ) {
    // eslint-disable-next-line no-console
    console.log('Event data from iframe.', eventData)
  }

  const pushEvent = eventData.push && typeof eventData.push === 'string'
  const payload = eventData.payload || eventData.value
  const actionEventGame = eventData.action === 'game.location' && payload

  if (pushEvent || actionEventGame) {
    if (localStorage.getItem('show-events')) {
      // eslint-disable-next-line no-console
      console.log('event', event)
      // eslint-disable-next-line no-console
      console.log('pushEvent', pushEvent)
      // eslint-disable-next-line no-console
      console.log('actionEventGame', actionEventGame)
    }
    let unresolvedUrl = pushEvent ? eventData.push : payload

    if (unresolvedUrl.startsWith('http')) {
      try {
        const parsedURL = new URL(unresolvedUrl)
        unresolvedUrl = parsedURL.pathname + parsedURL.search
      } catch (e) {
        console.error(e)
      }
    }

    try {
      if (BASE_URL && unresolvedUrl.startsWith(BASE_URL)) {
        unresolvedUrl = unresolvedUrl.replace(BASE_URL, '')
      }
      const newRoute = router.resolve(unresolvedUrl)

      if (newRoute && newRoute.fullPath !== route.fullPath && !['Auth', 'AuthEmail'].includes(newRoute.name)) {
        return router.replace(newRoute).catch(() => {})
      }
      router.replace({ name: 'Home' }).catch(() => {})
    } catch {
      router.replace({ name: 'Home' }).catch(() => {})
    }
  }
}

function initWsDGA() {
  if (process.env.DGA_URI && process.env.DGA_CASINO_ID) {
    dga.connect(process.env.DGA_URI, process.env.DGA_CASINO_ID, userCurrency.value?.code)
  }
}

function fetchPromoList() {
  userPromoLoad()
    .then((data) => {
      userBonusesStore.updateProgramAvailable(data?.userPromo?.programsAvailable ?? [])
      userBonusesStore.updateActiveStepLoaded()

      if (data?.userPromo?.activeStep) {
        userBonusesStore.updateActiveStep(data.userPromo.activeStep)
        userBonusesStore.updateUserProgram(data.userPromo.activeStep.userProgram)
        userBonusesStore.updateBonusGameIdsList(data.userPromo.activeStep.gameIdsList)
      }
    })
    .catch((e) => {
      consoleLogAPI(e)
    })
}

function fetchPromoTypes() {
  getContentListLoad(null, { type: 'promotion_types_v2' })
    .then((res) => {
      const { propsContentList } = res
      const promoTypes = propsContentList
        .map((promoType) => ({ id: promoType.id, ...JSON.parse(promoType.props) }))
        .sort((a, b) => a.id - b.id)

      promoStore.setPromoTypes(promoTypes)
    })
    .catch(consoleLogAPI)
}

function clearQueryParams() {
  const query = { ...route.query }
  let hasParams = false
  for (const param of LIST_OF_GET_PARAMS_TO_CLEAR) {
    if (query[param]) {
      hasParams = true
      delete query[param]
    }
  }
  if (hasParams) {
    router.replace({ query })
  }
}

async function updateIosOlympAppLink() {
  const isIOSPlatform = $helpers.platform.is.ios
  if (systemStore.alt || systemStore.altW || !isIOSPlatform) {
    return
  }

  if (Math.random() > 0.5) {
    const iosPWARoute = router.resolve({ name: 'IosWebApp' }).fullPath
    const iosPWAURL = `${location.origin}${iosPWARoute}`

    try {
      const { data } = await ApiServiceMain.iosMirrors.getDomain()
      if (data.domain) {
        afpl.updateAppLink('olymp', 'ios', data.domain)
      } else {
        afpl.updateAppLink('olymp', 'ios', iosPWAURL)
      }
    } catch (e) {
      console.error(e)
      afpl.updateAppLink('olymp', 'ios', iosPWAURL)
    }
  }
}

watch(
  locale,
  (val) => {
    document.documentElement.setAttribute('lang', LOCATIONS_LIST[val] || val)
  },
  { immediate: true },
)

onBeforeMount(() => {
  try {
    setTag('platform', process.env.PLATFORM)
    setTag('webview', process.env.WEBVIEW === '1')
  } catch {
    console.warn('Cannot set tags to sentry')
  }
})

if (config.loyaltyProgramEnabled && getAuth.value && !isWebViewPage.value && !restricted.value) {
  loyaltyStore.initLoyalty()
}

onMounted(() => {
  metrics.count('app:mounted', 1)

  if (isWebViewPage.value || restricted.value) {
    return
  }

  if (config.useTelegramMiniApp && !isWebView.value) {
    syncTelegramMiniApp()

    if (shouldInitTelegramMiniApp()) {
      initTelegramMiniApp()
    }
  }

  runNonCriticalTaskInWave(getIsShowBigWins, 3)
  runNonCriticalTaskInWave(setGtmUser, 2)

  router
    .isReady()
    .then(() => {
      if (isLiveChatPage.value) {
        return
      }

      PWA.checkPWA()

      if (route.query.promoreg) {
        localStorage.setItem(
          'promoreg',
          JSON.stringify({
            promoreg: route.query.promoreg,
            date: Date.now(),
          }),
        )
      }

      if (route.query.cinn) {
        localStorage.setItem('cinn', '1')
      }
      if (route.query.v) {
        localStorage.setItem('appVersion', route.query.v)
      }

      if (route.hash === '#payment_success' && isWebView.value) {
        postMessageFromWebviewToApp({ type: 'payment_success' })
        postMessageFromWebviewToApp({ type: 'modal', value: 'closed' })
      }

      runNonCriticalTaskInWave(initWsDGA, 0)
      runNonCriticalTaskInWave(fetchPromoTypes, 1)
      if (getAuth.value) {
        runNonCriticalTaskInWave(fetchPromoList, 2)

        if (route.hash === '#payment_decline' && route.query.old_money_order_uuid) {
          pendingDecline.value = { oldMoneyOrderUuid: route.query.old_money_order_uuid }
          popupQueue.requestRun()
        }
      }
      if (route.query.modal === 'reg') {
        ApiServiceMain.dwh.sendEvent('registration_initiated_modal', getCountryCode.value).catch(consoleLogAPI)
      }

      const isIOSPlatform = $helpers.platform.is.ios
      const isAndroidPlatform = $helpers.platform.is.android
      if (PWA.isPwa.value && (isIOSPlatform || isAndroidPlatform) && !mainStore.app_type) {
        mainStore.updateAppType(APP_TYPES.PWA)
        if (isIOSPlatform) {
          mainStore.updateAppId(APP_IDS.ios)
          mainStore.updateMbUuid(MB_UUIDS.iosPwa)
        } else if (isAndroidPlatform) {
          mainStore.updateAppId(APP_IDS.android)
        }
      }

      window.addEventListener('message', listenIframe)
      window.addEventListener('click', handleAllClicks)

      clearQueryParams()
    })
    .catch(console.error)

  initRecaptchaWarmupAfterInteraction()
  initFingerprintWarmupAfterInteraction()

  if (showUnderDev.value) {
    return
  }

  updateIosOlympAppLink()
})

onBeforeUnmount(() => {
  window.removeEventListener('message', listenIframe)
  window.removeEventListener('click', handleAllClicks)
})

provide('queries', queries)
provide('appModal', appModal.value)
provide('dwh', dwhSendEvent)
</script>
